/**
 * Pure view model for the Ad Engine debug page (spec §11 Chunk 2).
 *
 * Everything here is a function of `IAdProject` + the `freshness` map the router returns —
 * no React, no tRPC — so the rail glyphs, the `run next` target, the keyboard cursor and the
 * "why is this stale?" copy are all unit-testable without a DOM.
 *
 * v1 has 8 stages and no `context`/`cast` stages (those were cut from the prototype), so the
 * prototype's first two views collapse into one read-only `setup` view: bindings, notes,
 * product/brand basics and the format. `setup` is view 1; the 8 stages are views 2–9.
 */
import { AD_ENV_PLATE_GROUP_ID } from "@aix/vidmax-shared/src/adEngine/staleness"
import { EAdFreshness, EAdMouth, EAdPacing, EAdStage } from "@aix/vidmax-shared/src/schemas/adProject"
import type { IAdProject, IAdTake } from "@aix/vidmax-shared/src/schemas/adProject"

export const AD_ENGINE_SETUP_VIEW = "setup" as const

export type TAdEngineView = typeof AD_ENGINE_SETUP_VIEW | EAdStage

/** Pipeline order — the rail, `[`/`]`, and `run next` all read this one list. */
export const AD_ENGINE_STAGE_ORDER: readonly EAdStage[] = [
  EAdStage.SCRIPT,
  EAdStage.SPEECH,
  EAdStage.SHOTS,
  EAdStage.KEYFRAME,
  EAdStage.COMPILE,
  EAdStage.GENERATE,
  EAdStage.TIMELINE,
  EAdStage.RENDER,
]

export const AD_ENGINE_VIEWS: readonly TAdEngineView[] = [AD_ENGINE_SETUP_VIEW, ...AD_ENGINE_STAGE_ORDER]

export const isStageView = (view: TAdEngineView): view is EAdStage => view !== AD_ENGINE_SETUP_VIEW

/** Guards the `?stage=` deep link — an unknown value falls back to `setup` rather than blanking. */
export const isAdEngineView = (value: unknown): value is TAdEngineView =>
  typeof value === "string" && (AD_ENGINE_VIEWS as readonly string[]).includes(value)

const VIEW_LABELS: Record<TAdEngineView, string> = {
  [AD_ENGINE_SETUP_VIEW]: "setup",
  [EAdStage.SCRIPT]: "script",
  [EAdStage.SPEECH]: "speech",
  [EAdStage.SHOTS]: "shots",
  [EAdStage.KEYFRAME]: "keyframes",
  [EAdStage.COMPILE]: "compile",
  [EAdStage.GENERATE]: "generate",
  [EAdStage.TIMELINE]: "timeline",
  [EAdStage.RENDER]: "render",
}

export const viewLabel = (view: TAdEngineView): string => VIEW_LABELS[view]

/** `1` → setup, `2`–`9` → the eight stages. `0` is unbound: v1 has nine views, not ten. */
export const viewForDigit = (digit: string): TAdEngineView | undefined => {
  const index = Number(digit) - 1
  if (!Number.isInteger(index) || index < 0) return undefined
  return AD_ENGINE_VIEWS[index]
}

/** `[` / `]` — clamped, never wraps, so repeated presses park at the ends. */
export const stepView = (current: TAdEngineView, delta: number): TAdEngineView => {
  const index = AD_ENGINE_VIEWS.indexOf(current)
  const next = Math.min(AD_ENGINE_VIEWS.length - 1, Math.max(0, (index < 0 ? 0 : index) + delta))
  return AD_ENGINE_VIEWS[next] ?? current
}

// ───────────────────────────── freshness ─────────────────────────────

/** Rail state per stage. `PENDING` is not a freshness — it is "jobs are in flight right now". */
export enum EAdRailState {
  FRESH = "fresh",
  STALE = "stale",
  EMPTY = "empty",
  PENDING = "pending",
}

const GLYPHS: Record<EAdRailState, string> = {
  [EAdRailState.FRESH]: "●",
  [EAdRailState.STALE]: "◐",
  [EAdRailState.EMPTY]: "○",
  [EAdRailState.PENDING]: "◍",
}

export const glyphForRailState = (state: EAdRailState): string => GLYPHS[state]

export const railStateForStage = (
  stage: EAdStage,
  freshness: Partial<Record<EAdStage, EAdFreshness>>,
  pending: readonly EAdStage[],
): EAdRailState => {
  if (pending.includes(stage)) return EAdRailState.PENDING
  const value = freshness[stage]
  if (value === EAdFreshness.FRESH) return EAdRailState.FRESH
  if (value === EAdFreshness.STALE) return EAdRailState.STALE
  return EAdRailState.EMPTY
}

/** `run next` target: the first stage that is not FRESH. Undefined when the pipeline is done. */
export const nextNonFreshStage = (freshness: Partial<Record<EAdStage, EAdFreshness>>): EAdStage | undefined =>
  AD_ENGINE_STAGE_ORDER.find((stage) => freshness[stage] !== EAdFreshness.FRESH)

/**
 * Only the two generation stages hold takes. Defined here rather than in `types.ts` because this
 * module is the one that walks take groups — and `types.ts` already imports `TAdEngineView` from
 * here, so owning it there made the two files a cycle (`circular:check`, a pre-push gate).
 */
export type TAdTakeStage = EAdStage.KEYFRAME | EAdStage.GENERATE

// ───────────────────────────── live vs orphaned take groups ─────────────────────────────

/**
 * The ids a stage's take groups are *supposed* to be keyed by right now: shot ids (plus the
 * location plate, which is a take group but not a shot) for KEYFRAME, compiled clip ids for
 * GENERATE. Leaving the plate out made a pending plate invisible to the poll loop.
 */
const liveTakeIds = (doc: IAdProject, stage: TAdTakeStage): string[] =>
  stage === EAdStage.KEYFRAME
    ? [
        ...(doc.keyframes?.takes[AD_ENV_PLATE_GROUP_ID] ? [AD_ENV_PLATE_GROUP_ID] : []),
        ...(doc.shots ?? []).map((shot) => shot.id),
      ]
    : (doc.plan?.clips ?? []).map((clip) => clip.id)

const takeGroupsFor = (doc: IAdProject, stage: TAdTakeStage): Record<string, IAdTake[]> =>
  (stage === EAdStage.KEYFRAME ? doc.keyframes?.takes : doc.video?.takes) ?? {}

/**
 * Take groups that still belong to a current shot/clip.
 *
 * Re-running SHOTS (or COMPILE) under a new id scheme orphans the old groups — staleness never
 * deletes anything (D18) — so any aggregate built with `Object.values(takes)` silently counts
 * takes for cuts that no longer exist. The Nerva "Manual 1" doc carries 5 such groups
 * (`s01`, `shot_1`…`shot_4`) against 10 real shots; an orphan stuck at `pending` would keep the
 * poll loop alive forever. `TakeGrid` was already safe because it maps over `doc.shots`.
 */
export const liveTakeGroups = (doc: IAdProject, stage: TAdTakeStage): IAdTake[][] => {
  const takes = takeGroupsFor(doc, stage)
  return liveTakeIds(doc, stage)
    .map((id) => takes[id] ?? [])
    .filter((group) => group.length > 0)
}

/** Groups left behind by an id-scheme change — surfaced so the drift is visible, not silent. */
export const orphanTakeGroupIds = (doc: IAdProject, stage: TAdTakeStage): string[] => {
  const live = new Set(liveTakeIds(doc, stage))
  return Object.keys(takeGroupsFor(doc, stage))
    .filter((id) => !live.has(id))
    .sort()
}

const hasPendingTake = (doc: IAdProject, stage: TAdTakeStage): boolean =>
  liveTakeGroups(doc, stage).some((group) => group.some((take) => take.status === "pending"))

/** Stages holding at least one un-settled job — drives the spinner and the poll loop. */
export const pendingStagesOf = (doc: IAdProject): EAdStage[] => {
  const stages: EAdStage[] = []
  if (doc.speech && Object.values(doc.speech.lines).some((line) => line.status === "pending")) {
    stages.push(EAdStage.SPEECH)
  }
  if (doc.keyframes && hasPendingTake(doc, EAdStage.KEYFRAME)) stages.push(EAdStage.KEYFRAME)
  if (doc.video && hasPendingTake(doc, EAdStage.GENERATE)) stages.push(EAdStage.GENERATE)
  return stages
}

export const hasPendingWork = (doc: IAdProject): boolean => pendingStagesOf(doc).length > 0

/**
 * How many jobs are actually in flight, per stage.
 *
 * The continuous page is nine screens tall, so work can be running well outside the viewport;
 * the header needs a count to show and a stage to jump to, not just "something is pending".
 */
export const pendingCountsByStage = (doc: IAdProject): Partial<Record<EAdStage, number>> => {
  const counts: Partial<Record<EAdStage, number>> = {}
  const speech = Object.values(doc.speech?.lines ?? {}).filter((line) => line.status === "pending").length
  if (speech > 0) counts[EAdStage.SPEECH] = speech
  const countTakes = (stage: TAdTakeStage): number =>
    liveTakeGroups(doc, stage).reduce((sum, list) => sum + list.filter((take) => take.status === "pending").length, 0)
  const keyframe = countTakes(EAdStage.KEYFRAME)
  if (keyframe > 0) counts[EAdStage.KEYFRAME] = keyframe
  const video = countTakes(EAdStage.GENERATE)
  if (video > 0) counts[EAdStage.GENERATE] = video
  return counts
}

/** Total jobs in flight across every stage. */
export const pendingTotal = (doc: IAdProject): number =>
  Object.values(pendingCountsByStage(doc)).reduce((sum, count) => sum + count, 0)

/** Takes that came back failed, per group — surfaced so a dead generation is not silent. */
export const failedTakeCount = (doc: IAdProject, stage: TAdTakeStage): number =>
  liveTakeGroups(doc, stage).reduce((sum, list) => sum + list.filter((t) => t.status === "failed").length, 0)

/** Stages downstream of `stage` — what a rerun or an edit puts at risk. */
export const downstreamStages = (stage: EAdStage): EAdStage[] =>
  AD_ENGINE_STAGE_ORDER.slice(AD_ENGINE_STAGE_ORDER.indexOf(stage) + 1)

/** Spec §3.5 "Stage input table" in prose — the honest answer to "why is this stale?". */
export const STAGE_UPSTREAM_INPUTS: Record<EAdStage, string> = {
  [EAdStage.SCRIPT]: "formatId, bindings, context, overrides.script",
  [EAdStage.SPEECH]: "script line texts (per line), the voice binding",
  [EAdStage.SHOTS]: "script, every speech duration, overrides.shots",
  [EAdStage.KEYFRAME]: "shot prompt + framing, avatar/environment/product bindings, the identity anchor",
  [EAdStage.COMPILE]: "selected keyframe take ids, shots",
  [EAdStage.GENERATE]: "the compiled plan",
  [EAdStage.TIMELINE]: "selected video take ids, speech",
  [EAdStage.RENDER]: "tracksJson",
}

/** The track editor reads `tracksJson`, which only TIMELINE writes — `o` is a no-op before that. */
export const OPEN_IN_EDITOR_BLOCKED_REASON = "run TIMELINE first — the editor reads tracksJson"

export const canOpenInEditor = (freshness: Partial<Record<EAdStage, EAdFreshness>>): boolean =>
  freshness[EAdStage.TIMELINE] === EAdFreshness.FRESH

/** COMPILE and TIMELINE are pure — they rerun themselves when stale (D18). */
export const isPureStage = (stage: EAdStage): boolean => stage === EAdStage.COMPILE || stage === EAdStage.TIMELINE

export interface IWhyStale {
  stale: boolean
  storedHash?: string
  inputs: string
  downstream: EAdStage[]
  lastError?: string
  autoReruns: boolean
}

export const whyStale = (
  doc: IAdProject,
  stage: EAdStage,
  freshness: Partial<Record<EAdStage, EAdFreshness>>,
): IWhyStale => ({
  stale: freshness[stage] === EAdFreshness.STALE,
  ...(doc.stages[stage]?.inputHash ? { storedHash: doc.stages[stage]?.inputHash } : {}),
  inputs: STAGE_UPSTREAM_INPUTS[stage],
  downstream: downstreamStages(stage).filter((s) => freshness[s] === EAdFreshness.STALE),
  ...(doc.stages[stage]?.lastError ? { lastError: doc.stages[stage]?.lastError } : {}),
  autoReruns: isPureStage(stage),
})

// ───────────────────────────── selection cursor ─────────────────────────────

/** Selection is a `kind:id` string so ↑/↓ can walk one flat list per view (prototype parity). */
export enum EAdSelectionKind {
  BINDING = "bind",
  LINE = "line",
  SPEECH = "speech",
  SHOT = "shot",
  CLIP = "clip",
  STAGE = "stage",
}

export interface IAdSelection {
  kind: EAdSelectionKind
  id: string
}

export const selectionId = (kind: EAdSelectionKind, id: string): string => `${kind}:${id}`

export const parseSelection = (selection: string | null): IAdSelection | null => {
  if (!selection) return null
  const separator = selection.indexOf(":")
  if (separator <= 0) return null
  const kind = selection.slice(0, separator)
  const id = selection.slice(separator + 1)
  const known = Object.values(EAdSelectionKind).find((value) => value === kind)
  if (!known || !id) return null
  return { kind: known, id }
}

/** The ordered row/card ids ↑/↓ walks in the given view. */
export const selectableIds = (view: TAdEngineView, doc: IAdProject): string[] => {
  if (view === AD_ENGINE_SETUP_VIEW) {
    return Object.keys(doc.bindings).map((slotId) => selectionId(EAdSelectionKind.BINDING, slotId))
  }
  if (view === EAdStage.SCRIPT) {
    return (doc.script?.lines ?? []).map((line) => selectionId(EAdSelectionKind.LINE, line.id))
  }
  if (view === EAdStage.SPEECH) {
    return (doc.script?.lines ?? []).map((line) => selectionId(EAdSelectionKind.SPEECH, line.id))
  }
  if (view === EAdStage.SHOTS || view === EAdStage.KEYFRAME) {
    return (doc.shots ?? []).map((shot) => selectionId(EAdSelectionKind.SHOT, shot.id))
  }
  if (view === EAdStage.COMPILE || view === EAdStage.GENERATE) {
    return (doc.plan?.clips ?? []).map((clip) => selectionId(EAdSelectionKind.CLIP, clip.id))
  }
  return []
}

/** ↑/↓. From "nothing selected", ↓ lands on the first row and ↑ on the last. */
export const moveSelection = (ids: readonly string[], current: string | null, delta: number): string | null => {
  if (ids.length === 0) return null
  const index = current ? ids.indexOf(current) : -1
  if (index < 0) return delta > 0 ? (ids[0] ?? null) : (ids[ids.length - 1] ?? null)
  const next = Math.min(ids.length - 1, Math.max(0, index + delta))
  return ids[next] ?? null
}

// ───────────────────────────── keyframe gate ─────────────────────────────

/** The keyframe hard gate (C3): every shot needs a settled selected take before COMPILE. */
export const everyShotHasSelection = (doc: IAdProject): boolean => {
  const shots = doc.shots ?? []
  if (shots.length === 0) return false
  return shots.every((shot) => {
    const selected = doc.keyframes?.selected[shot.id]
    if (selected === undefined) return false
    return doc.keyframes?.takes[shot.id]?.[selected]?.status === "completed"
  })
}

/** The first ON_CAMERA shot owns the identity anchor; rerolling it stales every other keyframe. */
export const anchorShotId = (doc: IAdProject): string | undefined =>
  (doc.shots ?? []).find((shot) => shot.mouth === EAdMouth.ON_CAMERA)?.id

export const spendRatio = (doc: IAdProject): number =>
  doc.costCapUsd > 0 ? Math.min(1, doc.spend.usd / doc.costCapUsd) : 0

// ───────────────────────────── media ─────────────────────────────

/**
 * Doc media keys (speech `audioKey`, take `url`) are private R2 objects the browser cannot
 * fetch. `adEngine.get` returns `mediaUrls`, a display-only map of key → signed URL; a take that
 * settled with a foreign absolute URL is playable as-is. Anything else has no playable source.
 */
export const mediaSrc = (key: string | undefined, mediaUrls: Record<string, string>): string | undefined => {
  if (!key) return undefined
  return mediaUrls[key] ?? (/^https?:\/\//.test(key) ? key : undefined)
}

// ───────────────────────────── keyboard dispatch ─────────────────────────────

/**
 * Every key the page answers to. The hook maps one intent to one action, so the key table and
 * the guard rules below are testable without a DOM (this package's vitest runs in `node`).
 */
export enum EAdKeyIntent {
  VIEW = "view",
  STEP = "step",
  MOVE = "move",
  RUN = "run",
  POLL = "poll",
  APPROVE = "approve",
  EDIT = "edit",
  WHY = "why",
  OPEN = "open",
  JSON = "json",
  HELP = "help",
  ESCAPE = "escape",
}

export type TAdKeyIntent =
  | { intent: EAdKeyIntent.VIEW; view: TAdEngineView }
  | { intent: EAdKeyIntent.STEP; delta: 1 | -1 }
  | { intent: EAdKeyIntent.MOVE; delta: 1 | -1 }
  | {
      intent: Exclude<EAdKeyIntent, EAdKeyIntent.VIEW | EAdKeyIntent.STEP | EAdKeyIntent.MOVE>
    }

/** The bits of a `KeyboardEvent` the dispatcher reads — so tests can pass a plain object. */
export interface IAdKeyEvent {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  repeat?: boolean
  /** `event.target`'s tag name, upper case (`INPUT`, `TEXTAREA`, …). */
  targetTagName?: string | undefined
  targetIsContentEditable?: boolean | undefined
}

const TYPING_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"])

/**
 * Prototype parity (`ad-engine-admin-prototype.html`, bottom `keydown` handler): keys are dead
 * while the operator is typing, and any meta/ctrl/alt combination belongs to the browser.
 */
export const isAdEngineKeyIgnored = (event: IAdKeyEvent): boolean =>
  event.metaKey ||
  event.ctrlKey ||
  event.altKey ||
  event.repeat === true ||
  // Synthetic/undecodable events (CDP `Input.dispatchKeyEvent` noise, IME composition) carry no
  // usable key — treating them as a match is what made one `]` press skip several stages.
  event.key === "" ||
  event.key === "Unidentified" ||
  TYPING_TAGS.has(event.targetTagName ?? "") ||
  event.targetIsContentEditable === true

/** Pure key table. Returns `null` for keys the page does not own. */
export const adEngineKeyIntent = (event: IAdKeyEvent): TAdKeyIntent | null => {
  if (isAdEngineKeyIgnored(event)) return null
  const key = event.key
  if (key === "?") return { intent: EAdKeyIntent.HELP }
  if (key === "Escape") return { intent: EAdKeyIntent.ESCAPE }
  if (/^[1-9]$/.test(key)) {
    const view = viewForDigit(key)
    return view ? { intent: EAdKeyIntent.VIEW, view } : null
  }
  if (key === "]") return { intent: EAdKeyIntent.STEP, delta: 1 }
  if (key === "[") return { intent: EAdKeyIntent.STEP, delta: -1 }
  if (key === "ArrowDown") return { intent: EAdKeyIntent.MOVE, delta: 1 }
  if (key === "ArrowUp") return { intent: EAdKeyIntent.MOVE, delta: -1 }
  if (key === "r") return { intent: EAdKeyIntent.RUN }
  if (key === "p") return { intent: EAdKeyIntent.POLL }
  if (key === "a") return { intent: EAdKeyIntent.APPROVE }
  if (key === "e") return { intent: EAdKeyIntent.EDIT }
  if (key === "w") return { intent: EAdKeyIntent.WHY }
  if (key === "o") return { intent: EAdKeyIntent.OPEN }
  if (key === "j") return { intent: EAdKeyIntent.JSON }
  return null
}

/** Absent on older docs means the default, which is `tight`. */
export const effectivePacing = (pacing: EAdPacing | undefined): EAdPacing => pacing ?? EAdPacing.TIGHT
